import { createPayment, isPaymentPreconditionError } from '@/services/purchase/createPayment';
import { camelizeKeys } from 'humps';
import * as types from 'constants/ActionTypes';
import {
  getPaymentPayload,
  hasPaymentErrorCode,
  paymentFailureHandler,
  paymentPollingHandler,
} from './payment';
import { requestPayment, updatePurchase } from './purchase';
import { normalizeStripeResult } from './stripeShared';
import cardFactory from '../../payments/core/factories/card';

/**
 * Flags whether the client-side Stripe steps (Payment Element validation and ConfirmationToken
 * creation) are in progress. Drives the "Finalizar compra" button loading/disabled state without
 * touching isUpdating/fetchingPayment, which would unmount the Payment Element mid-submit.
 *
 * @param {boolean} submitting - Whether the client-side Stripe flow is running.
 * @returns {{type: string, submitting: boolean}} The SET_STRIPE_SUBMITTING action.
 */
export function setStripeSubmitting(submitting) {
  return { type: types.SET_STRIPE_SUBMITTING, submitting };
}

/**
 * Initiates a Stripe payment attempt using a ConfirmationToken created on the client.
 *
 * @param {string} purchaseToken - The token associated with the purchase.
 * @param {Object} paymentData - The Stripe-specific payment data.
 * @param {string} paymentData.confirmationTokenId - Stripe ConfirmationToken ID.
 * @param {Object} paymentData.cardValues - Card and billing information, already in backend format
 *   (incl. `card_holder_name`); spread as-is into the payload.
 * @returns {Function} A thunk action that dispatches actions based on the Stripe payment flow.
 */
export function stripePaymentAttempt(purchaseToken, { confirmationTokenId, cardValues }) {
  return async (dispatch, getState) => {
    dispatch(updatePurchase(true));
    dispatch(requestPayment());

    try {
      let resolvePollResult;
      const pollResult = new Promise((resolve) => {
        resolvePollResult = resolve;
      });
      const selectedPaymentMethod = getState().purchase.get('selectedPaymentMethod');
      const extra = selectedPaymentMethod?.extra ?? {};
      const stripePayment = cardFactory.create({ engine: 'stripe' });
      const enginePayload = await stripePayment.createPayload(cardValues, {
        ...extra,
        stripeConfirmationToken: confirmationTokenId,
      });
      const paymentPayload = {
        ...getPaymentPayload('credit_card', 'stripe', cardValues),
        ...enginePayload,
        ...cardValues,
      };

      let normalizedResult;

      const response = await createPayment(purchaseToken, paymentPayload, {
        onReceivePayment: (rawResponse) => {
          const normalized = normalizeStripeResult(camelizeKeys(rawResponse));
          normalizedResult = normalized;

          if (normalized.outcome === 'requires_client_action') {
            resolvePollResult(normalized);
            return normalized;
          }

          // paymentPollingHandler is the single source of truth for
          // completed / pending / failed / aborted (already Stripe-aware).
          paymentPollingHandler(dispatch, purchaseToken, rawResponse);

          // Resolve the awaited result for terminal states; keep polling while pending.
          if (normalized.outcome !== 'pending' || rawResponse.status === 'aborted') {
            resolvePollResult(normalized);
          }

          return normalized;
        },
      });

      if (response?.polls?.payment) {
        return pollResult;
      }

      return normalizedResult || normalizeStripeResult(camelizeKeys(response));
    } catch (error) {
      const errorReason = error?.errorReason || error?.code || 'default';

      // A precondition refusal, or a total below the method's minimum (MER-701), is not a decline:
      // wrapped as a Stripe error, its `errorReason` would win the handler's Stripe branch and tell
      // the buyer their card was rejected.
      const passesThrough =
        isPaymentPreconditionError(error) || hasPaymentErrorCode(error, 'invalid_amount');
      paymentFailureHandler(
        dispatch,
        passesThrough ? error : { paymentEngine: 'stripe', errorReason },
      );

      return {
        outcome: 'failed',
        errorReason,
        raw: error,
      };
    }
  };
}

export default stripePaymentAttempt;
